import { apiFetch } from './client'

/**
 * Tela 4 (sessao 35) - lista de cargos, SO LEITURA.
 *
 * Contrato: `GET /api/jobs` (modules/job_architecture/api/router.py), permissao
 * `job_architecture.job.read`, resposta = LISTA pura (sem total). O tenant e o da
 * SESSAO (A132): a SPA nao envia X-Tenant-ID.
 *
 * S27-6 (cargos SEM salario): a resposta da API traz `salary_range` e `description`;
 * o mapeamento abaixo DESCARTA os dois de proposito -- o tipo `Job` desta tela nao
 * tem onde guardar salario, entao nenhum componente consegue mostra-lo por engano.
 * (O dado ainda trafega na resposta: achado registrado para decisao, nao desta tela.)
 *
 * TETO: 20 paginas de 200 (4.000 cargos). Acima disso, ERRO EXPLICITO -- nunca uma
 * lista truncada em silencio.
 */

export type JobLevel = 'junior' | 'pleno' | 'senior' | 'specialist'
export type JobStatus = 'active' | 'inactive' | 'archived'

export const JOB_LEVELS: readonly JobLevel[] = ['junior', 'pleno', 'senior', 'specialist']
export const JOB_STATUSES: readonly JobStatus[] = ['active', 'inactive', 'archived']

export interface Job {
  id: string
  code: string
  name: string
  /** J1 (s47): null = nivel nao informado (o cliente usa outra escala). */
  level: JobLevel | null
  careerName: string | null
  cboCode: string | null
  status: JobStatus
}

interface JobResponse {
  id: string
  code: string
  name: string
  /** J1 (s47): null = nivel nao informado (o cliente usa outra escala). */
  level: JobLevel | null
  career_name: string | null
  cbo_code: string | null
  status: JobStatus
}

export const JOBS_PAGE_SIZE = 200
export const JOBS_MAX_PAGES = 20

export class TooManyJobsError extends Error {
  constructor() {
    super(`mais de ${JOBS_PAGE_SIZE * JOBS_MAX_PAGES} cargos`)
    this.name = 'TooManyJobsError'
  }
}

function toJob(r: JobResponse): Job {
  // Campo a campo, de proposito: nada alem destes 7 entra na tela (S27-6).
  return {
    id: r.id,
    code: r.code,
    name: r.name,
    level: r.level,
    careerName: r.career_name,
    cboCode: r.cbo_code,
    status: r.status,
  }
}

export async function fetchAllJobs(): Promise<Job[]> {
  const todos: Job[] = []
  for (let pagina = 0; pagina < JOBS_MAX_PAGES; pagina++) {
    const itens = await apiFetch<JobResponse[]>(
      `/api/jobs?limit=${JOBS_PAGE_SIZE}&offset=${pagina * JOBS_PAGE_SIZE}`,
    )
    for (const r of itens) todos.push(toJob(r))
    if (itens.length < JOBS_PAGE_SIZE) return todos
  }
  throw new TooManyJobsError()
}

export interface JobFilter {
  text: string
  levels: ReadonlySet<JobLevel>
  statuses: ReadonlySet<JobStatus>
}

/** Minusculas e sem acento: "Técnico" e "tecnico" se encontram. */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

/**
 * Filtra e ordena por nome (pt-BR). Conjunto vazio = sem filtro naquela dimensao.
 * O texto procura em nome, codigo, carreira e CBO (s69 / 2m: tambem no formato 0000-00 que a tela mostra).
 */
export function filterJobs(jobs: readonly Job[], f: JobFilter): Job[] {
  const alvo = normalizar(f.text)
  return jobs
    .filter((j) => f.levels.size === 0 || (j.level !== null && f.levels.has(j.level)))
    .filter((j) => f.statuses.size === 0 || f.statuses.has(j.status))
    .filter(
      (j) =>
        alvo === '' ||
        [j.name, j.code, j.careerName ?? '', j.cboCode ?? '', formatCbo(j.cboCode) ?? ''].some((c) => normalizar(c).includes(alvo)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

export const JOBS_QUERY_KEY = ['jobs'] as const

/**
 * s61 / E1 (decisao de Marcelo 19/10/2026) - coluna "Descricao" da lista: `GET /api/job-description-states`
 * (permissao `job_architecture.description.read`) devolve, por cargo, a ultima versao PUBLICADA (ou null) e se ha
 * RASCUNHO -- sem o texto. Cargo que nao aparece = sem descricao. Leitura defensiva: so entra item com `job_id`
 * texto (resposta inesperada vira "sem estado", nunca quebra a lista).
 */
export interface DescriptionState {
  publishedVersion: number | null
  hasDraft: boolean
}

export type DescriptionFilter = 'published' | 'draft' | 'none'
export const DESCRIPTION_FILTERS: readonly DescriptionFilter[] = ['published', 'draft', 'none']

export async function fetchDescriptionStates(): Promise<Map<string, DescriptionState>> {
  const r = await apiFetch<unknown>('/api/job-description-states')
  const mapa = new Map<string, DescriptionState>()
  if (!Array.isArray(r)) return mapa
  for (const e of r as Record<string, unknown>[]) {
    if (!e || typeof e.job_id !== 'string') continue
    mapa.set(e.job_id, {
      publishedVersion: typeof e.published_version === 'number' ? e.published_version : null,
      hasDraft: e.has_draft === true,
    })
  }
  return mapa
}

/** O cargo atende a ALGUM dos estados marcados (conjunto vazio = sem filtro). */
export function matchesDescription(
  state: DescriptionState | undefined,
  filtros: ReadonlySet<DescriptionFilter>,
): boolean {
  if (filtros.size === 0) return true
  if (filtros.has('published') && state?.publishedVersion != null) return true
  if (filtros.has('draft') && state?.hasDraft === true) return true
  if (filtros.has('none') && (state === undefined || (state.publishedVersion == null && !state.hasDraft))) return true
  return false
}

export const DESCRIPTION_STATES_QUERY_KEY = ['job-description-states'] as const

/**
 * s69 / 2m (Marcelo 28/10/2026 14:30, "C2 sim"): o CBO e DIGITADO na ficha do cargo. O dominio guarda 6 digitos
 * (CBOCode, ADR-0018 D2); a tela mostra e aceita no formato oficial 0000-00. Vazio limpa o campo.
 */
export function cboDigits(texto: string): string {
  return texto.replace(/\D/g, '')
}

export function formatCbo(cbo: string | null | undefined): string | null {
  if (!cbo) return null
  const d = cboDigits(cbo)
  return d.length === 6 ? `${d.slice(0, 4)}-${d.slice(4)}` : cbo
}

export async function updateJobCbo(jobId: string, digits: string): Promise<void> {
  await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/basics`, { method: 'PATCH', body: { cbo_code: digits } })
}

/** s69 / 2o (V1): candidatos da TABELA OFICIAL da CBO para o nome do cargo (o servidor nao inventa codigo; sem IA).
 * s69 / 2p (ADR-0035): + ``via = 'atividades'`` (a ocupacao veio pelo que o cargo FAZ) e ``reason`` (motivo da IA). */
export interface CboSuggestion {
  code: string
  title: string
  matched?: string
  via?: 'titulo' | 'sinonimo' | 'atividades'
  reason?: string
}

/** s69 / 2p (ADR-0035 D6): selo "CBO a revisar" do CBO gravado (null sem CBO). */
export interface CboReview {
  code: string
  exists: boolean
  rank: number | null
  to_review: boolean
}

export interface CboSuggestions {
  available: boolean
  items: CboSuggestion[]
  review?: CboReview | null
  /** viga = a IA escolheu entre os candidatos oficiais; sistema = so a tabela (etapa 1) */
  mode?: 'viga' | 'sistema'
  degraded?: boolean
}

export async function fetchCboSuggestions(jobId: string): Promise<CboSuggestions> {
  return apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/cbo-suggestions`)
}

/** s69 / 2p (ADR-0035 D4): pede a VIGA (IA so escolhe entre candidatos oficiais). O servidor responde com a etapa 1
 * quando a IA da empresa ou o recurso ``cbo_ia`` esta desligado; se o pedido falhar (sem permissao de IA, teto),
 * cai na tabela (GET, sem IA) -- a pessoa sempre recebe sugestoes oficiais. */
export async function suggestCbo(jobId: string): Promise<CboSuggestions> {
  try {
    return await apiFetch<CboSuggestions>('/api/ai/cbo-suggestions', { method: 'POST', body: { job_id: jobId } })
  } catch {
    const r = await fetchCboSuggestions(jobId)
    return { ...r, mode: 'sistema' }
  }
}

export const cboReviewKey = (jobId: string, cbo: string | null) => ['cbo-review', jobId, cbo] as const

/** s69 / 2r (Marcelo 29/10/2026 08:44, "como crio um novo cargo"): o codigo interno do cargo (slug, ADR-0018 D5) sai do
 * NOME -- a pessoa nao ve nem digita codigo (2m tirou o codigo da tela). Minusculas, sem acento, hifens, 2 a 64. */
export function jobCodeFromName(nome: string): string {
  const s = normalizar(nome)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '')
  return s.length >= 2 ? s : ''
}

/** Cria o cargo (POST /api/jobs; o servidor confere tudo de novo). Devolve o id do cargo novo. */
export async function createJob(nome: string, cboDigitos: string): Promise<string> {
  const body: Record<string, string> = { code: jobCodeFromName(nome), name: nome.trim() }
  if (cboDigitos) body.cbo_code = cboDigitos
  const r = await apiFetch<{ id: string }>('/api/jobs', { method: 'POST', body })
  return r.id
}

/** s69 / 2s: situacao do cargo na empresa -- so ativo <-> inativo (arquivar fica fora). */
export async function updateJobStatus(jobId: string, status: 'active' | 'inactive'): Promise<void> {
  await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/status`, { method: 'PATCH', body: { status } })
}
